import type { FormKitNode } from '@formkit/core'

export function rootClasses(
  sectionName: string,
  node: FormKitNode,
): Record<string, boolean> {
  const key = `${node.props.type}__${sectionName}`
  const semanticKey = `formkit-${sectionName}`
  const familyKey = node.props.family
    ? `family:${node.props.family}__${sectionName}`
    : ''
  const memoKey = `${key}__${familyKey}`
  if (!(memoKey in classes)) {
    const sectionClasses = classes[key] ?? globals[sectionName] ?? {}
    sectionClasses[semanticKey] = true
    if (familyKey in classes) {
      classes[memoKey] = { ...classes[familyKey], ...sectionClasses }
    } else {
      classes[memoKey] = sectionClasses
    }
  }
  return classes[memoKey] ?? { [semanticKey]: true }
}

const classes: Record<string, Record<string, boolean>> = {
  'family:button__input': {
    button: true,
    'is-primary': true,
    'w-auto': true,
  },
  button__input: {
    button: true,
  },
  checkbox__wrapper: {
    flex: true,
    'items-center': true,
    'gap-2': true,
  },
  checkbox__input: {
    'formkit-checkbox': true,
    checkbox: true,
    'appearance-none': true,
    'checked:bg-primary': true,
    'checked:border-primary': true,
    'checked:text-scheme-main': true,
    'hover:border-button-hover-border-color': true,
    'peer-hover:border-button-hover-border-color': true,
  },
  checkbox__label: {
    'formkit-label': true,
    checkbox: true,
  },
  checkbox__decorator: {
    hidden: true,
  },
  date__input: {
    'formkit-date': true,
    input: true,
    'text-base': true,
    'text-grey-light': true,
    'pr-3': true,
    'hover:border-button-hover-border-color': true,
    'active:border-button-focus-border-color': true,
    'focus:border-button-focus-border-color': true,
    'hover:text-scheme-light': true,
    'active:text-scheme-light': true,
    'focus:text-scheme-light': true,
    relative: true,
  },
  file__input: {
    hidden: true,
  },
  file__label: {
    button: true,
  },
  file__noFiles: {
    hidden: true,
  },
  file__fileRemove: {
    'flex items-center flex-row-reverse': true,
    'text-base': true,
    'leading-6': true,
    'ml-0 tablet:ml-3.5': true,
  },
  file__fileRemoveIcon: {
    'inline-block': true,
    'text-sm': true,
    relative: true,
    'text-background': true,
    'button rounded-full is-tiny is-danger p-0': true,
    'hover:bg-danger-light': true,
    'peer-hover:bg-danger-light': true,
  },
  radio__wrapper: {
    flex: true,
    'items-center': true,
    'gap-2': true,
  },
  radio__input: {
    radio: true,
    'formkit-radio': true,
    'appearance-none': true,
    'w-4 h-4': true,
    'border-2': true,
    'border-button-border-color': true,
    'rounded-full': true,
    'peer-hover:border-button-hover-border-color': true,
    'hover:border-button-hover-border-color': true,
    'checked:text-primary': true,
    'checked:border-primary': true,
    'checked:hover:border-primary': true,
    'checked:peer-hover:border-primary': true,
    'checked:active:border-primary': true,
    'active:border-primary': true,
    'peer-hover:active:border-primary': true,
  },
  radio__label: {
    'formkit-label': true,
    radio: true,
    'text-base': true,
    'text-scheme-light': true,
  },
  radio__legend: {
    'formkit-label': true,
    label: true,
  },
  radio__decorator: {
    hidden: true,
  },
  select__inner: {
    select: true,
    'text-button-color': true,
    'after:text-primary': true,
  },
  select__input: {
    select: true,
    'hover:border-button-hover-border-color': true,
  },
  select__selectIcon: {
    hidden: true,
  },
  textarea__inner: {
    'text-base': true,
    'text-button-color': true,
  },
  textarea__input: {
    textarea: true,
    'hover:border-button-hover-border-color': true,
    'active:border-button-focus-border-color': true,
    'focus:border-button-focus-border-color': true,
  },
  time__input: {
    'formkit-time': true,
    input: true,
    'text-button-color': true,
    'text-base': true,
    'hover:border-button-hover-border-color': true,
    'active:border-button-focus-border-color': true,
    'focus:border-button-focus-border-color': true,
  },
}

const globals: Record<string, Record<string, boolean>> = {
  outer: {
    'mb-[29px]': true,
  },
  label: {
    label: true,
  },
  legend: {
    label: true,
  },
  input: {
    input: true,
    'text-base': true,
    'text-button-color': true,
    'hover:border-button-hover-border-color': true,
    'active:border-button-focus-border-color': true,
    'focus:border-button-focus-border-color': true,
    'rounded-none': true,
  },
  textarea: {
    textarea: true,
    'rounded-none': true,
  },
  button: {
    button: true,
  },
  checkbox: {
    input: true,
    label: true,
  },
  radio: {
    radio: true,
  },
  message: {
    'text-danger': true,
    'mb-1.5': true,
    'text-xs': true,
  },
  help: {
    'text-base': true,
  },
}
